import React from 'react'
import { InputNumber, Radio } from 'antd'
import { Button } from '@surecompanies/core_components'
import { RequiredMark } from '../shared/finance.styles'
import {
  RadioOptionRow,
  SyncField,
  SyncFieldHint,
  SyncFieldLabel,
  SyncFieldRow,
  SyncRowActions,
  SyncStepGroup,
  SyncStepGroupTitle,
  SyncSubmitError,
} from './ClientBillingProfileManagement.styles'
import { HubspotSyncEmailList } from './HubspotSyncEmailList'
import { BILLING_PAYMENT_TYPE_OPTIONS } from './hubspotSync.helpers'
import { MAX_DUE_DAY, MIN_DUE_DAY } from './types'
import type { useHubspotSyncNewCompanies } from './useHubspotSyncNewCompanies'

interface HubspotSyncNewCompanyRowsProps {
  state: ReturnType<typeof useHubspotSyncNewCompanies>
}

/**
 * The companies HubSpot found that we hold no billing profile for, set up in
 * place in the sync banner. Each row is submitted on its own: it creates the
 * profile and, when this admin may grant access, hands funding access to the
 * addresses still ticked.
 */
const HubspotSyncNewCompanyRows = ({
  state,
}: HubspotSyncNewCompanyRowsProps) => (
  <>
    {state.rows.map(
      ({ item, paymentType, dueDay, emails, isReady, submitError }) => {
        const id = item.hubspotCompanyId
        return (
          <SyncStepGroup key={id}>
            <SyncStepGroupTitle>{item.hubspotCompanyName}</SyncStepGroupTitle>

            <SyncField>
              <SyncFieldLabel>Client Email</SyncFieldLabel>
              <HubspotSyncEmailList
                emails={emails}
                canGrant={state.canGrant}
                isGranted={(email) => state.isGranted(id, email)}
                onGrantedChange={(email, granted) =>
                  state.setGranted(id, email, granted)
                }
                onRemove={(email) =>
                  state.setEmails(
                    id,
                    emails.filter((kept) => kept !== email)
                  )
                }
              />
            </SyncField>

            <SyncFieldRow>
              <SyncField>
                <SyncFieldLabel>
                  Payment Type <RequiredMark aria-hidden>*</RequiredMark>
                </SyncFieldLabel>
                <Radio.Group
                  value={paymentType}
                  onChange={(event) =>
                    state.setPaymentType(id, event.target.value)
                  }
                >
                  <RadioOptionRow>
                    {BILLING_PAYMENT_TYPE_OPTIONS.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </RadioOptionRow>
                </Radio.Group>
              </SyncField>

              <SyncField>
                <SyncFieldLabel>
                  Due Day <RequiredMark aria-hidden>*</RequiredMark>
                </SyncFieldLabel>
                <InputNumber
                  value={dueDay}
                  min={MIN_DUE_DAY}
                  max={MAX_DUE_DAY}
                  precision={0}
                  placeholder="15"
                  onChange={(value) =>
                    state.setDueDay(
                      id,
                      typeof value === 'number' ? value : undefined
                    )
                  }
                />
              </SyncField>
            </SyncFieldRow>

            {emails.length === 0 ? (
              <SyncFieldHint>
                This profile will be created with no recipients, so its invoices
                will not be delivered until one is added.
              </SyncFieldHint>
            ) : null}

            {submitError ? (
              <SyncSubmitError role="alert">
                Not created: {submitError}
              </SyncSubmitError>
            ) : null}

            <SyncRowActions>
              <Button
                type="primary"
                size="small"
                disabled={!isReady || state.isSubmittingAny}
                loading={state.isSubmittingCompany(id)}
                onClick={() => state.handleSubmitCompany(id)}
              >
                Submit
              </Button>
            </SyncRowActions>
          </SyncStepGroup>
        )
      }
    )}
  </>
)

export { HubspotSyncNewCompanyRows }
